/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import {
  Employee,
  EmployeeType,
  OrganizationalStructure,
  HRMetrics,
  AuditStatus,
  Vendor,
  TPRMWorkflow,
  OrganizationalCosts,
  ComplianceChecklist,
  ChecklistItem
} from '../types';
import {
  EMPLOYEE_CONFIG,
  EXTERNAL_AUDITOR_CONFIG,
  DEPARTMENT_BUDGET_ALLOCATION,
  ORGANIZATIONAL_BENCHMARKS,
  VENDOR_CATEGORIES,
  AUDIT_REQUIREMENTS
} from '../constants/orgManagement';

export class OrgManagementService {
  private employees: Employee[] = [];
  private vendors: Vendor[] = [];
  private workflows: TPRMWorkflow[] = [];
  private checklists: ComplianceChecklist[] = [];
  private audits: AuditStatus[] = [];
  private currentTick = 0;

  // Headcount Management
  hireEmployee(type: EmployeeType, currentOrgSize: number = 0): Employee {
    const config = EMPLOYEE_CONFIG[type];
    if (!config) {
      throw new Error(`Employee type ${type} not configured`);
    }

    // Calculate salary based on experience and market conditions
    const experienceVariation = (Math.random() - 0.5) * 0.3; // ±15%
    const marketAdjustment = this.getMarketAdjustment(type, currentOrgSize);
    const salary = Math.round(config.baseSalary * (1 + experienceVariation) * marketAdjustment);

    // Calculate total compensation (base + bonus + equity)
    const bonusMultiplier = type in Object.keys(EMPLOYEE_CONFIG).slice(0, 7) ? 0.4 : 0.2; // Executives get higher bonus
    const equityValue = salary * (currentOrgSize < 50 ? 0.5 : currentOrgSize < 200 ? 0.3 : 0.1); // Early stage gives more equity
    const totalCompensation = salary + (salary * bonusMultiplier) + equityValue;

    const employee: Employee = {
      id: `emp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      name: this.generateEmployeeName(type),
      salary,
      totalCompensation: Math.round(totalCompensation),
      experience: Math.max(0, config.experience + Math.round((Math.random() - 0.5) * 4)),
      productivity: config.productivity * (0.9 + Math.random() * 0.2), // 90-110% of baseline
      morale: 70 + Math.round(Math.random() * 20), // 70-90 initial morale
      trainingLevel: Math.max(0, Math.floor(config.experience / 3)),
      certifications: this.generateInitialCertifications(type),
      hireDate: this.currentTick,
      department: config.department,
      reportsTo: this.assignManager(type, this.employees)
    };

    this.employees.push(employee);
    return employee;
  }

  calculateOrganizationalStructure(): OrganizationalStructure {
    const departments: Record<string, any> = {};
    const hierarchy: Record<string, string[]> = {};

    // Group employees by department
    this.employees.forEach(employee => {
      if (!departments[employee.department]) {
        departments[employee.department] = {
          headcount: 0,
          budgetAllocation: 0,
          avgSalary: 0,
          avgExperience: 0,
          productivity: 0,
          morale: 0
        };
      }

      const dept = departments[employee.department];
      dept.headcount++;
      dept.budgetAllocation += employee.totalCompensation;
      dept.avgSalary += employee.salary;
      dept.avgExperience += employee.experience;
      dept.productivity += employee.productivity;
      dept.morale += employee.morale;

      // Build hierarchy
      if (employee.reportsTo) {
        if (!hierarchy[employee.reportsTo]) {
          hierarchy[employee.reportsTo] = [];
        }
        hierarchy[employee.reportsTo].push(employee.id);
      }
    });

    // Calculate averages
    Object.values(departments).forEach((dept: any) => {
      if (dept.headcount > 0) {
        dept.avgSalary = Math.round(dept.avgSalary / dept.headcount);
        dept.avgExperience = Math.round(dept.avgExperience / dept.headcount);
        dept.productivity = Math.round((dept.productivity / dept.headcount) * 100) / 100;
        dept.morale = Math.round(dept.morale / dept.headcount);
      }
    });

    // Calculate management layers and span of control
    const managementLevels = this.calculateManagementLevels();
    const avgSpanOfControl = this.calculateAverageSpanOfControl(hierarchy);

    return {
      totalEmployees: this.employees.length,
      departments,
      managementLayers: managementLevels,
      spanOfControl: avgSpanOfControl,
      hierarchy
    };
  }

  calculateHRMetrics(): HRMetrics {
    const totalEmployees = this.employees.length;
    if (totalEmployees === 0) {
      return {
        turnoverRate: 0,
        avgTimeToFill: 0,
        trainingHoursPerEmployee: 0,
        employeeSatisfaction: 0,
        diversityMetrics: { gender: 0, ethnicity: 0, age: 0 },
        performanceMetrics: { highPerformers: 0, improvementNeeded: 0, avgProductivity: 0 }
      };
    }

    const avgProductivity = this.employees.reduce((sum, emp) => sum + emp.productivity, 0) / totalEmployees;
    const avgMorale = this.employees.reduce((sum, emp) => sum + emp.morale, 0) / totalEmployees;
    const avgExperience = this.employees.reduce((sum, emp) => sum + emp.experience, 0) / totalEmployees;

    const highPerformers = this.employees.filter(emp => emp.productivity >= 1.2).length;
    const improvementNeeded = this.employees.filter(emp => emp.productivity < 0.8).length;

    return {
      turnoverRate: 0.12, // 12% annual turnover (simulated)
      avgTimeToFill: 42, // days (simulated)
      trainingHoursPerEmployee: 40, // hours per year (simulated)
      employeeSatisfaction: Math.round(avgMorale),
      diversityMetrics: {
        gender: 38, // percentage (simulated)
        ethnicity: 42, // percentage (simulated)
        age: Math.round(avgExperience + 25) // average age
      },
      performanceMetrics: {
        highPerformers: Math.round((highPerformers / totalEmployees) * 100),
        improvementNeeded: Math.round((improvementNeeded / totalEmployees) * 100),
        avgProductivity: Math.round(avgProductivity * 100) / 100
      }
    };
  }

  calculateOrganizationalCosts(): OrganizationalCosts {
    const totalSalaries = this.employees.reduce((sum, emp) => sum + emp.salary, 0);
    const benefitsRate = 0.28; // 28% benefits rate
    const benefits = totalSalaries * benefitsRate;

    const training = this.employees.length * 2000; // $2K per employee annually
    const recruiting = this.employees.length * 3000; // $3K per employee annually (attrition)
    const externalAudit = this.audits.reduce((sum, audit) => sum + audit.cost, 0);
    const consulting = totalSalaries * 0.05; // 5% of salaries for consulting
    const software = totalSalaries * 0.15; // 15% of salaries for software/tools
    const officeSpace = this.employees.length * 12000; // $12K per employee annually

    return {
      salaries: totalSalaries,
      benefits,
      training,
      recruiting,
      externalAudit,
      consulting,
      software,
      officeSpace,
      total: totalSalaries + benefits + training + recruiting + externalAudit + consulting + software + officeSpace
    };
  }

  // TPRM Management
  onboardVendor(vendorData: Partial<Vendor>): Vendor {
    const vendor: Vendor = {
      id: `vendor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: vendorData.name || 'New Vendor',
      type: vendorData.type || 'Software',
      criticality: vendorData.criticality || 'Medium',
      riskScore: this.calculateInitialRiskScore(vendorData),
      status: 'Onboarding',
      contractValue: vendorData.contractValue || 0,
      contractStart: this.currentTick,
      contractEnd: this.currentTick + (365 * 2), // 2 year default
      slaCompliance: 100,
      lastReview: this.currentTick,
      nextReview: this.currentTick + 90, // 90 days
      services: vendorData.services || [],
      dataClassification: vendorData.dataClassification || 'Internal',
      geographicRegions: vendorData.geographicRegions || ['US'],
      certifications: vendorData.certifications || [],
      dependencies: vendorData.dependencies || []
    };

    this.vendors.push(vendor);

    // Create TPRM workflow
    const workflow: TPRMWorkflow = {
      vendorId: vendor.id,
      stage: 'InitialRequest',
      startDate: this.currentTick,
      currentStep: 'Initial Risk Assessment',
      completedSteps: [],
      blockers: [],
      nextAction: 'Complete risk assessment questionnaire',
      responsibleParty: 'vendor_manager',
      targetCompletion: this.currentTick + 14 // 2 weeks
    };

    this.workflows.push(workflow);
    return vendor;
  }

  updateTPRMWorkflow(workflowId: string, updates: Partial<TPRMWorkflow>): void {
    const workflowIndex = this.workflows.findIndex(w => w.vendorId === workflowId);
    if (workflowIndex !== -1) {
      this.workflows[workflowIndex] = { ...this.workflows[workflowIndex], ...updates };
    }
  }

  // Audit Management
  scheduleAudit(type: AuditStatus['type'], targetDate: number): AuditStatus {
    const config = AUDIT_REQUIREMENTS[type];
    const audit: AuditStatus = {
      type,
      status: 'NotStarted',
      targetDate,
      auditor: this.selectAuditor(type),
      findings: { critical: 0, high: 0, medium: 0, low: 0, informational: 0 },
      score: 0,
      cost: config.costRange[0] + Math.round(Math.random() * (config.costRange[1] - config.costRange[0])),
      remediationRequired: false
    };

    this.audits.push(audit);
    return audit;
  }

  // Compliance Checklist Management
  createChecklist(type: ComplianceChecklist['type'], title: string): ComplianceChecklist {
    const checklist: ComplianceChecklist = {
      id: `checklist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      title,
      description: this.getChecklistDescription(type),
      category: this.getChecklistCategory(type),
      items: this.generateChecklistItems(type),
      progress: 0,
      dueDate: this.currentTick + 90, // 90 days default
      assignedTo: 'compliance_team',
      status: 'NotStarted',
      lastUpdated: this.currentTick
    };

    this.checklists.push(checklist);
    return checklist;
  }

  updateChecklistItem(checklistId: string, itemId: string, updates: Partial<ChecklistItem>): void {
    const checklist = this.checklists.find(c => c.id === checklistId);
    if (checklist) {
      const itemIndex = checklist.items.findIndex(item => item.id === itemId);
      if (itemIndex !== -1) {
        checklist.items[itemIndex] = { ...checklist.items[itemIndex], ...updates };
        checklist.progress = this.calculateChecklistProgress(checklist.items);
        checklist.lastUpdated = this.currentTick;
        checklist.status = checklist.progress === 100 ? 'Completed' : 'InProgress';
      }
    }
  }

  // Simulation Updates
  updateSimulation(newTick: number): void {
    this.currentTick = newTick;

    // Update employee morale and productivity
    this.updateEmployeeMetrics();

    // Process vendor workflows
    this.processTPRMWorkflows();

    // Update audit statuses
    this.updateAuditStatuses();

    // Update checklist due dates
    this.updateChecklistStatuses();
  }

  // Helper methods
  private generateEmployeeName(type: EmployeeType): string {
    const firstNames = ['Alex', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Avery', 'Quinn', 'Sage', 'River'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];

    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];

    return `${firstName} ${lastName}`;
  }

  private getMarketAdjustment(type: EmployeeType, orgSize: number): number {
    const config = EMPLOYEE_CONFIG[type];
    let baseAdjustment = 1.0;

    // Market demand adjustment
    switch (config.marketDemand) {
      case 'Very High': baseAdjustment *= 1.25; break;
      case 'High': baseAdjustment *= 1.15; break;
      case 'Medium': baseAdjustment *= 1.05; break;
      case 'Low': baseAdjustment *= 0.95; break;
    }

    // Company size adjustment (smaller companies pay more for talent)
    if (orgSize < 50) baseAdjustment *= 1.1;
    else if (orgSize > 500) baseAdjustment *= 0.95;

    return baseAdjustment;
  }

  private generateInitialCertifications(type: EmployeeType): string[] {
    const certMap: Record<string, string[]> = {
      [EmployeeType.SecurityEngineer]: ['CISSP', 'Security+'],
      [EmployeeType.ComplianceAnalyst]: ['CAMS', 'CRCM'],
      [EmployeeType.AMLAnalyst]: ['CAMS', 'ACAMS'],
      [EmployeeType.InternalAuditor]: ['CIA', 'CISA'],
      [EmployeeType.DevOpsEngineer]: ['AWS Certified', 'Kubernetes'],
      [EmployeeType.DataAnalyst]: ['Tableau', 'Power BI']
    };

    return certMap[type] || [];
  }

  private assignManager(type: EmployeeType, currentEmployees: Employee[]): string | undefined {
    // For executives, no manager
    const executiveTypes = [EmployeeType.CEO, EmployeeType.CTO, EmployeeType.CFO, EmployeeType.CRO, EmployeeType.CCO, EmployeeType.COO];
    if (executiveTypes.includes(type)) {
      return undefined;
    }

    // Find appropriate manager from current employees
    const config = EMPLOYEE_CONFIG[type];
    const potentialManagers = currentEmployees.filter(emp => {
      const empConfig = EMPLOYEE_CONFIG[emp.type];
      return empConfig && empConfig.level > config.level && empConfig.department === config.department;
    });

    if (potentialManagers.length > 0) {
      // Select manager with fewest direct reports
      return potentialManagers.reduce((prev, current) =>
        (this.getDirectReportCount(prev.id) < this.getDirectReportCount(current.id)) ? prev : current
      ).id;
    }

    return undefined;
  }

  private getDirectReportCount(managerId: string): number {
    return this.employees.filter(emp => emp.reportsTo === managerId).length;
  }

  private calculateManagementLevels(): number {
    let levels = 0;
    let currentLevel = this.employees.filter(emp => !emp.reportsTo);

    while (currentLevel.length > 0) {
      levels++;
      currentLevel = this.employees.filter(emp =>
        currentLevel.some(manager => manager.id === emp.reportsTo)
      );
    }

    return levels;
  }

  private calculateAverageSpanOfControl(hierarchy: Record<string, string[]>): number {
    const managerCounts = Object.values(hierarchy);
    if (managerCounts.length === 0) return 0;

    const totalReports = managerCounts.reduce((sum, reports) => sum + reports.length, 0);
    return Math.round((totalReports / managerCounts.length) * 10) / 10;
  }

  private calculateInitialRiskScore(vendorData: Partial<Vendor>): number {
    let baseScore = 50;

    // Adjust based on vendor type
    if (vendorData.type && VENDOR_CATEGORIES[vendorData.type]) {
      baseScore *= VENDOR_CATEGORIES[vendorData.type].riskMultiplier;
    }

    // Adjust based on criticality
    const criticalityMultipliers = { 'Low': 0.7, 'Medium': 1.0, 'High': 1.3, 'Critical': 1.6 };
    if (vendorData.criticality) {
      baseScore *= criticalityMultipliers[vendorData.criticality];
    }

    // Adjust based on data classification
    const dataMultipliers = { 'Public': 0.5, 'Internal': 1.0, 'Confidential': 1.5, 'Restricted': 2.0 };
    if (vendorData.dataClassification) {
      baseScore *= dataMultipliers[vendorData.dataClassification];
    }

    return Math.min(100, Math.max(0, Math.round(baseScore)));
  }

  private selectAuditor(type: AuditStatus['type']): string {
    const firms = {
      'SOC2': ['Deloitte', 'PwC', 'EY', 'KPMG', 'BDO', 'RSM'],
      'PCI': ['Coalfire', 'FireEye', 'SecureTrust', 'Verizone', 'Trustwave'],
      'Financial': ['Deloitte', 'PwC', 'EY', 'KPMG'],
      'Regulatory': ['Federal Reserve', 'OCC', 'FDIC', 'CFPB', 'SEC']
    };

    const availableFirms = firms[type] || ['Unknown Firm'];
    return availableFirms[Math.floor(Math.random() * availableFirms.length)];
  }

  private getChecklistDescription(type: ComplianceChecklist['type']): string {
    const descriptions = {
      'SOC2': 'SOC 2 Type II compliance preparation and audit readiness',
      'PCI': 'PCI DSS compliance requirements and security standards',
      'Regulatory': 'Regulatory examination preparation and response',
      'InternalAudit': 'Internal audit program and control testing',
      'TPRM': 'Third-Party Risk Management vendor assessments',
      'IncidentResponse': 'Security incident response and recovery procedures'
    };

    return descriptions[type] || 'Compliance checklist';
  }

  private getChecklistCategory(type: ComplianceChecklist['type']): string {
    const categories = {
      'SOC2': 'Security Compliance',
      'PCI': 'Payment Security',
      'Regulatory': 'Regulatory Compliance',
      'InternalAudit': 'Internal Controls',
      'TPRM': 'Vendor Management',
      'IncidentResponse': 'Security Operations'
    };

    return categories[type] || 'Compliance';
  }

  private generateChecklistItems(type: ComplianceChecklist['type']): ChecklistItem[] {
    // This would typically load from a template, but for now we'll generate basic items
    const baseItems = [
      {
        id: 'item_1',
        description: 'Review and update policies',
        requirement: 'Policies must be current and approved',
        category: 'Policy Management',
        completed: false,
        evidence: '',
        notes: '',
        priority: 'High' as const,
        dueDate: this.currentTick + 30,
        dependencies: []
      },
      {
        id: 'item_2',
        description: 'Complete control testing',
        requirement: 'All controls must be tested and documented',
        category: 'Control Testing',
        completed: false,
        evidence: '',
        notes: '',
        priority: 'High' as const,
        dueDate: this.currentTick + 45,
        dependencies: ['item_1']
      },
      {
        id: 'item_3',
        description: 'Gather evidence documentation',
        requirement: 'Evidence must support control effectiveness',
        category: 'Evidence Collection',
        completed: false,
        evidence: '',
        notes: '',
        priority: 'Medium' as const,
        dueDate: this.currentTick + 60,
        dependencies: ['item_2']
      }
    ];

    return baseItems;
  }

  private calculateChecklistProgress(items: ChecklistItem[]): number {
    if (items.length === 0) return 0;
    const completedItems = items.filter(item => item.completed).length;
    return Math.round((completedItems / items.length) * 100);
  }

  private updateEmployeeMetrics(): void {
    this.employees.forEach(employee => {
      // Update morale based on various factors
      if (this.currentTick % 10 === 0) { // Update every 10 ticks
        const randomChange = (Math.random() - 0.5) * 10;
        employee.morale = Math.max(0, Math.min(100, employee.morale + randomChange));

        // Update productivity based on morale
        const moraleImpact = (employee.morale - 50) / 100; // -0.5 to +0.5
        employee.productivity = Math.max(0.5, Math.min(1.5,
          EMPLOYEE_CONFIG[employee.type].productivity + moraleImpact * 0.2
        ));
      }
    });
  }

  private processTPRMWorkflows(): void {
    this.workflows.forEach(workflow => {
      if (this.currentTick % 7 === 0) { // Process weekly
        // Simulate workflow progress
        const progressChance = Math.random();
        if (progressChance > 0.7) {
          // Move to next stage
          workflow.completedSteps.push(workflow.currentStep);

          // Determine next stage
          const stages = ['InitialRequest', 'RiskAssessment', 'DueDiligence', 'ContractReview', 'ExecutiveApproval', 'Onboarding', 'ActiveMonitoring'];
          const currentIndex = stages.indexOf(workflow.stage);
          if (currentIndex < stages.length - 1) {
            workflow.stage = stages[currentIndex + 1] as TPRMWorkflow['stage'];
          }
        }
      }
    });
  }

  private updateAuditStatuses(): void {
    this.audits.forEach(audit => {
      if (audit.status === 'NotStarted' && this.currentTick >= audit.startDate) {
        audit.status = 'InProgress';
      }

      if (audit.status === 'InProgress') {
        // Simulate audit progress
        const progress = (this.currentTick - (audit.startDate || this.currentTick)) / 30; // 30 ticks = audit duration
        if (progress >= 1.0) {
          audit.status = 'Completed';
          audit.actualDate = this.currentTick;
          audit.score = 75 + Math.round(Math.random() * 20); // 75-95 score

          // Generate findings
          audit.findings = {
            critical: Math.random() > 0.9 ? 1 : 0,
            high: Math.random() > 0.7 ? Math.floor(Math.random() * 3) : 0,
            medium: Math.random() > 0.5 ? Math.floor(Math.random() * 5) : 0,
            low: Math.random() > 0.3 ? Math.floor(Math.random() * 8) : 0,
            informational: Math.floor(Math.random() * 10)
          };

          audit.remediationRequired = audit.findings.critical > 0 || audit.findings.high > 2;
        }
      }
    });
  }

  private updateChecklistStatuses(): void {
    this.checklists.forEach(checklist => {
      if (checklist.status === 'InProgress' && checklist.dueDate < this.currentTick) {
        checklist.status = 'Overdue';
      }
    });
  }

  // Getters for UI
  getEmployees(): Employee[] {
    return [...this.employees];
  }

  getVendors(): Vendor[] {
    return [...this.vendors];
  }

  getWorkflows(): TPRMWorkflow[] {
    return [...this.workflows];
  }

  getChecklists(): ComplianceChecklist[] {
    return [...this.checklists];
  }

  getAudits(): AuditStatus[] {
    return [...this.audits];
  }

  getEmployeesByDepartment(department: string): Employee[] {
    return this.employees.filter(emp => emp.department === department);
  }

  getActiveAudits(): AuditStatus[] {
    return this.audits.filter(audit => audit.status === 'InProgress');
  }

  getVendorsByStatus(status: Vendor['status']): Vendor[] {
    return this.vendors.filter(vendor => vendor.status === status);
  }
}

// Export singleton instance
export const orgManagementService = new OrgManagementService();